import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../constants/gameEventFormBroadcastFields'
import { GAME, LIVESTREAM_TYPE_OPTIONS_MAPPER } from '../../constants/livestreamTypeOptionsMapper'
import ORGANISATION_TYPES from '../../constants/OrganisationTypes'
import { setEventStartTime, setEventEndTime } from './setEventTime'

const getGameStateTrackingValues = (trackingType) => {
  switch (trackingType) {
    case 'Auto':
      return { auto: true, manual: false }
    case 'Manual':
      return { auto: false, manual: true }
    case 'Disabled':
    default:
      return { auto: false, manual: false }
  }
}

const findTeamsInEventTeams = (teams, eventTeams) => {
  if (!eventTeams || eventTeams.length === 0) return null

  const teamsMap = new Map(teams?.map((team) => [team?.id, team]))
  // Also index by teamId for season-teams (e.g. competition orgs)
  teams?.forEach((team) => {
    if (team?.teamId) teamsMap.set(team.teamId, team)
  })
  const matchingTeams = eventTeams
    .filter((eventTeam) => eventTeam.teamId && teamsMap.has(eventTeam.teamId))
    .map((eventTeam) => teamsMap.get(eventTeam.teamId))

  return matchingTeams.length > 0 ? matchingTeams : null
}

const getGameEventInitialValues = ({
  event,
  dateClick,
  currentTeam,
  teams,
  allowOrgYoutube,
  useOrgYoutube,
  orgType,
  courts,
  orgSharedRevenue,
}) => {
  const {
    place,
    court,
    notes,
    externalId,
    hasStats,
    broadcast,
    media,
    ytBroadcastDetails,
    eventTeams,
    competitionName,
    competitionLogoUrl,
    thirdPartyBroadcastUrl,
    pairedGame,
  } = event || {}

  const {
    streamDestination,
    streamDestination2,
    autoStart,
    autoStop,
    autofollowEnabled,
    isCommentaryOn,
    commentaryMode,
    courtTopLeftLogoUrl,
    courtTopRightLogoUrl,
    courtBottomLeftLogoUrl,
    courtBottomRightLogoUrl,
    courtCenterLogoUrl,
    streamerSaveUrl,
    type,
    gameStateTrackingType,
    sharedRevenue,
    scoreboardSource,
  } = broadcast || {}

  const { broadcastId, thumbnailUrl, broadcastTitle, visibility } = ytBroadcastDetails || {}

  // Slot 0 / slot 1 containers now preserve the backend eventTeams array order; which
  // slot is HOME is tracked separately by `homeTeamIndex` (non-swapping model), not by
  // the isHomeTeam flag position on the object itself.
  const isClub = orgType === ORGANISATION_TYPES.ORG_CLUB
  // New club games: default the slot-0 team to the club's own team (row 0 is the club-team
  // autocomplete) so the common single-team flow works out of the box, matching the prior
  // auto-fill behavior. The user can still change it. Federations and edits keep eventTeams.
  const homeTeam = !event && isClub ? (currentTeam ?? eventTeams?.[0]) : eventTeams?.[0]
  const awayTeam = eventTeams?.[1]
  const homeTeamIndexFromEvent = eventTeams?.findIndex((team) => team?.isHomeTeam) ?? -1
  let homeTeamIndex
  if (!event) {
    // New game: first row is home by default.
    homeTeamIndex = 0
  } else if (homeTeamIndexFromEvent >= 0) {
    homeTeamIndex = homeTeamIndexFromEvent
  } else {
    // Existing game with no home flag (legacy rows): a game always has exactly one home
    // team, so fall back to the first row rather than leaving the form with none.
    homeTeamIndex = 0
  }
  const matchingTeams = findTeamsInEventTeams(teams, eventTeams)
  const { auto: gameStateTrackingAuto, manual: gameStateTrackingManual } =
    getGameStateTrackingValues(gameStateTrackingType)

  return {
    fromDate: setEventStartTime(event, dateClick),
    startTime: setEventStartTime(event, dateClick),
    endTime: setEventEndTime(event, dateClick),
    teams: matchingTeams?.[0] ?? currentTeam,
    place: event ? place : '',
    // New events: when the org has a single court there is nothing to choose, preselect it.
    courtId: court?.id ?? (!event && courts?.length === 1 ? courts[0].id : ''),
    notes: event ? notes : null,
    externalId: externalId ?? null,
    hasStats: hasStats ?? false,
    autofollowEnabled: autofollowEnabled ?? true,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON]: isCommentaryOn ?? false,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE]: commentaryMode ?? 'OffSite',
    // Null on a stored stream means its tier has no ads; if the user then picks Plus/Pro the
    // toggle starts from the org default, exactly as the backend would seed it.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.SHARED_REVENUE]: sharedRevenue ?? Boolean(orgSharedRevenue),
    // A new event has no source yet; the picker shows the server's default until one is picked.
    [GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE]: scoreboardSource ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_START]: autoStart ?? true,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_STOP]: autoStop ?? true,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]: !!streamDestination || !!streamDestination2,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]: !!streamerSaveUrl,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE]: LIVESTREAM_TYPE_OPTIONS_MAPPER[GAME](type),
    [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION]: !useOrgYoutube ? streamDestination : null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_ADDITIONAL_STREAM_DESTINATION_ADDED]: !!streamDestination2,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION_2]: streamDestination2 ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]:
      !useOrgYoutube || !allowOrgYoutube
        ? null
        : {
            broadcastId: broadcastId ?? null,
            [GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_THUMBNAIL]: thumbnailUrl ?? null,
            [GAME_EVENT_FORM_BROADCAST_FIELDS.BROADCAST_TITLE]: broadcastTitle ?? '',
            [GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY]: visibility ?? '',
          },
    [GAME_EVENT_FORM_BROADCAST_FIELDS.THIRD_PARTY_BROADCAST_URL]: thirdPartyBroadcastUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME]: homeTeam ?? [],
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY]: awayTeam ?? [],
    [GAME_EVENT_FORM_BROADCAST_FIELDS.HOME_TEAM_INDEX]: homeTeamIndex,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME_LOGO]:
      homeTeam?.logoUrl ?? homeTeam?.teamLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY_LOGO]: awayTeam?.logoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO]: competitionLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_NAME]: competitionName ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO]: courtTopLeftLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO]: courtTopRightLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO]: courtBottomLeftLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO]: courtBottomRightLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO]: courtCenterLogoUrl ?? null,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.UPLOADED_MEDIAS]: media
      ? media.reduce((acc, obj) => {
          acc[obj.filename] = obj.id
          return acc
        }, {})
      : {},
    [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO]: gameStateTrackingAuto,
    [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL]: gameStateTrackingManual,
    studioOperatorId: event?.studioOperators?.[0]?.id ?? null,
    statisticianIds: event?.statisticians?.map((s) => s.id) ?? [],
    videoOperatorIds: event?.videoOperators?.map((o) => o.id) ?? [],
    // Federation game this club game is connected to. Authoritative on update (null clears it),
    // so it must be prefilled from the existing connection or a plain edit-save would wipe it.
    pairedGameId: pairedGame?.gameEventId ?? null,
  }
}

export default getGameEventInitialValues
